import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Row, Col, ListGroup, Button, Spinner, Alert, Modal, Form, Badge } from 'react-bootstrap';
import ReactCountryFlag from 'react-country-flag';
import { getTournament, addPlayersToTournament, removePlayerFromTournament } from '../../services/api';
import { Tournament, TournamentPlayer } from '../../models/Tournament';
import convertCountryCodeToAlpha2 from '../../utils/countryCodeConverter';

const TournamentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [players, setPlayers] = useState<TournamentPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showRemovePlayerModal, setShowRemovePlayerModal] = useState(false);
  const [playerNames, setPlayerNames] = useState('');
  const [addingPlayers, setAddingPlayers] = useState(false);
  const [removingPlayerId, setRemovingPlayerId] = useState<string | null>(null);
  const [playerToRemove, setPlayerToRemove] = useState<TournamentPlayer | null>(null);

  const fetchTournamentDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const tournamentData = await getTournament(id);
      setTournament(tournamentData);

      // Use the players data from the tournament response
      if (tournamentData.players && Array.isArray(tournamentData.players)) {
        setPlayers(tournamentData.players);
      } else {
        setPlayers([]);
      }
    } catch (err) {
      console.error('Error fetching tournament details:', err);
      setError('Failed to load tournament details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTournamentDetails();
  }, [fetchTournamentDetails]);

  const handleAddPlayers = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id || !playerNames.trim()) {
      return;
    }

    setAddingPlayers(true);

    try {
      // Split the comma-separated names and trim whitespace
      const names = playerNames.split(',').map((name: string) => name.trim()).filter((name: string) => name);

      if (names.length === 0) {
        return;
      }

      const addedPlayers = await addPlayersToTournament(id, names);

      // Update the players list with the newly added players
      setPlayers(prevPlayers => [...prevPlayers, ...addedPlayers]);

      // Close the modal and reset the form
      setShowAddPlayerModal(false);
      setPlayerNames('');
    } catch (err) {
      console.error('Error adding players:', err);
      setError('Failed to add players. Please try again.');
    } finally {
      setAddingPlayers(false);
    }
  };

  const confirmRemovePlayer = (player: TournamentPlayer) => {
    setPlayerToRemove(player);
    setShowRemovePlayerModal(true);
  };

  const handleRemovePlayer = async () => {
    if (!id || !playerToRemove) {
      return;
    }

    const playerId = playerToRemove.playerId;
    setRemovingPlayerId(playerId);

    try {
      await removePlayerFromTournament(id, playerId);

      // Remove the player from the local state
      setPlayers(prevPlayers => prevPlayers.filter(player => player.playerId !== playerId));

      // Close the modal
      setShowRemovePlayerModal(false);
      setPlayerToRemove(null);
    } catch (err) {
      console.error('Error removing player:', err);
      setError('Failed to remove player. Please try again.');
    } finally {
      setRemovingPlayerId(null);
    }
  };

  const handleCancelRemove = () => {
    setShowRemovePlayerModal(false);
    setPlayerToRemove(null);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!tournament) {
    return <Alert variant="warning">Tournament not found</Alert>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{tournament.name}</h2>
        <Link to="/tournaments" className="btn btn-outline-primary">Back to Tournaments</Link>
      </div>

      <Row>
        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <h4>Tournament Information</h4>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={5}><strong>Tournament ID:</strong></Col>
                <Col sm={7}>{tournament.tournamentId}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={5}><strong>Type:</strong></Col>
                <Col sm={7}>{tournament.type}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={5}><strong>Last Updated:</strong></Col>
                <Col sm={7}>{new Date(tournament.lastUpdated).toLocaleString()}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4>Players</h4>
              <Button variant="primary" size="sm" onClick={() => setShowAddPlayerModal(true)}>
                Add Players
              </Button>
            </Card.Header>
            <Card.Body>
              {players.length > 0 ? (
                <ListGroup variant="flush">
                  {players.map((player) => (
                    <ListGroup.Item key={player.playerId} className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div style={{ textAlign: 'left', width: '60%' }}>
                          <div className="d-flex flex-column">
                            <div className="d-flex align-items-center">
                              {player.nationality && (
                                <span className="me-2" title={player.nationality}>
                                  <ReactCountryFlag
                                    countryCode={convertCountryCodeToAlpha2(player.nationality)}
                                    svg
                                    style={{ width: '1.2em', height: '1.2em' }}
                                  />
                                </span>
                              )}
                              <Link to={`/players/${player.playerId}`} className="text-decoration-none">
                                <div className="fw-bold">{player.playerName}</div>
                              </Link>
                              {player.ageRange && (
                                <Badge bg="secondary" className="ms-2 py-1 px-2" style={{ fontSize: '0.7rem' }}>
                                  {player.ageRange}
                                </Badge>
                              )}
                            </div>
                            {player.dominantHand && <span className="me-2">{player.dominantHand}</span>}
                          </div>
                        </div>

                        <div className="col-6 col-sm-5 d-flex justify-content-end">
                          {/* Rating display */}
                          <div className="text-center me-2" style={{ minWidth: '45px' }}>
                            {player.threeMonthUtrRating ? (
                              <span className="badge bg-light text-dark">
                                {player.threeMonthUtrRating.toFixed(2)}
                              </span>
                            ) : player.singlesUtr ? (
                              <span className="badge bg-light text-dark">
                                {player.singlesUtr.toFixed(2)}
                              </span>
                            ) : (
                              <span className="badge bg-light text-dark">-</span>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div className="d-flex">
                            {/* playerId now refers to the internal Player.Id */}
                            <Link
                              to={`/players/${player.playerId}`}
                              state={{
                                fromTournament: true,
                                tournamentId: tournament?.tournamentId,
                                tournamentName: tournament?.name
                              }}
                              className="btn btn-sm btn-outline-info me-1 p-1 px-2"
                            >
                              <i className="fas fa-eye"></i>
                            </Link>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="p-1 px-2"
                              onClick={() => confirmRemovePlayer(player)}
                              disabled={removingPlayerId === player.playerId}
                            >
                              {removingPlayerId === player.playerId ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <i className="fas fa-trash-alt"></i>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-center p-4">
                  <p>No players in this tournament yet.</p>
                  <Button variant="primary" onClick={() => setShowAddPlayerModal(true)}>
                    Add Players
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Players Modal */}
      <Modal show={showAddPlayerModal} onHide={() => setShowAddPlayerModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Players to Tournament</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddPlayers}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Player Names (comma-separated)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter player names separated by commas (e.g., John Doe, Jane Smith)"
                value={playerNames}
                onChange={(e) => setPlayerNames(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Players will be added by name. If a player doesn't exist, they will be created.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddPlayerModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={addingPlayers}>
              {addingPlayers ? 'Adding Players...' : 'Add Players'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Remove Player Confirmation Modal */}
      <Modal show={showRemovePlayerModal} onHide={handleCancelRemove} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Player Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {playerToRemove && (
            <p>
              Are you sure you want to remove <strong>{playerToRemove.playerName}</strong> from this tournament?
              This action cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelRemove}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRemovePlayer}
            disabled={removingPlayerId !== null}
          >
            {removingPlayerId !== null ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Removing...
              </>
            ) : (
              'Remove Player'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TournamentDetails;
